import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SlLogout } from "react-icons/sl";
import { NavLink } from "react-router-dom";
import { useMedia } from "react-use";
import { Badge, Center, Skeleton, Stack } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconDiamondFilled } from "@tabler/icons-react";

import { LanguageSelect } from "@components/LanguageSelect";

import { getInitials } from "@utils/getInitials";
import { truncateWithEllipsis } from "@utils/truncateText";

import { getSidebarLinks } from "@data/sidebarLinks";

import { MAX_PAGE_SIZE, ROLES } from "@constants/index";

import { useSignOut } from "@hooks/mutations/user/useSignOut";
import { useGetAppointments } from "@hooks/queries/appointment/useGetAppointments";

import { useAuth } from "@contexts/auth";

import { mq } from "@styles/Utilities.styled";

import Logo from "@assets/misc/logo.webp";
import LogoIcon from "@assets/misc/logo-icon.svg?react";

import { StyledSidebar } from "./Sidebar.styled";


export const Sidebar = () => {
  const { t } = useTranslation();

  const { username, roles, id, permissions, permissionsLoading, resetAuthStates , canAccessPath, can } = useAuth();
  const assignedAppointments = permissions["appointments.view"] === "assigned";

  const [hasRunIAR, setHasRunIAR] = useState(false); // IAR = Incoming Appointment Request
  const [showUserInfo, setShowUserInfo] = useState(false);

  const isTouchDevice = useMediaQuery(`(pointer: coarse) and (hover: none)`);
  const isLargeDevice = useMedia(`${mq.large1}`); // Using react-use here because Mantine doesn't calculate this fast enough to prevent a flash since sidebar is re-rendered on every page change

  const { data: data1 } = useGetAppointments({
    isEnabled:
      // prettier-ignore
      (!hasRunIAR && true )&&
      !permissionsLoading && can("appointments.view"),
    filterBy: assignedAppointments ? id : "",
    category: "teleconsultation",
    status: "pending",
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE,
    },
  });
  const { data: data2 } = useGetAppointments({
    isEnabled:
      // prettier-ignore
      (!hasRunIAR && true )&&
      !permissionsLoading && can("appointments.view"),
    filterBy: assignedAppointments ? id : "",
    category: "homecare",
    status: "pending",
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE,
    },
  });
  const { mutate: signOut } = useSignOut();

  const totalNotifications =
    (data1?.pagination.total || 0) + (data2?.pagination.total || 0);

  useEffect(() => {
    //* Run the incoming appointments requests query only once
    if (!hasRunIAR) setHasRunIAR(true);
  }, []);

  return (
    <StyledSidebar>
      <ul className="sidebar-content">
        <li className="sidebar-item">
          {isLargeDevice ? <img src={Logo} alt="" /> : <LogoIcon />}
          {/* {isLargeDevice ? <LogoFull /> : <LogoIcon />} */}
        </li>
        {roles?.length ? (
          getSidebarLinks(canAccessPath).map(item => (
            <li className="sidebar-item" key={item.link}>
              <NavLink
                to={`${!item.isEnabled ? "#" : "/" + item.link}`}
                title={`${isLargeDevice ? "" : item.title}`}
                className={({ isActive }) => {
                  if (isActive && item.isEnabled) {
                    return "active";
                  }
                  // When `to` prop is "#", it wrongfully assumes the link is active
                  else if (isActive && !item.isEnabled) {
                    return "inactive";
                  } else return "";
                }}>
                <div className="icon-wrapper">
                  <item.iconOutline />
                  <item.iconFill />
                </div>
                <div className="title-wrapper">
                  <span className="title">{item.title}</span>
                  {/* Show admin icon for only admin links */}
                  {item.roles.length === 1 && item.roles[0] === "admin" && (
                    <>
                      <IconDiamondFilled
                        size={14}
                        className="admin-icon"
                        title={t("admin-only")}
                      />
                      <Badge
                        className="admin-badge"
                        color="var(--clr-notification)"
                        fz={10}>
                        ADMIN
                      </Badge>
                    </>
                  )}
                </div>
                {item.link.includes("appointments")
                  ? (data1 || data2) && (
                      <span className="notification">
                        {totalNotifications > 99 ? "99+" : totalNotifications}
                      </span>
                    )
                  : null}
              </NavLink>
            </li>
          ))
        ) : (
          <Center mt={"2.5rem"}>
            <Stack gap={"xs"}>
              <Skeleton h={30} w={{ base: 50, md: 200 }} />
              <Skeleton h={30} w={{ base: 50, md: 200 }} />
              <Skeleton h={30} w={{ base: 50, md: 200 }} />
              <Skeleton h={30} w={{ base: 50, md: 200 }} />
            </Stack>
          </Center>
        )}

        <li className="sidebar-item logout">
          <button
            title={t("logout")}
            onClick={() => {
              resetAuthStates();
              signOut({});
            }}>
            <SlLogout />
            <span className="title">{t("logout")}</span>
          </button>
        </li>
        <li className="sidebar-item">
          <LanguageSelect />
        </li>
        <li className="sidebar-item">
          <div className={`popup  ${showUserInfo ? "appear" : ""}`}>
            <p className="name">{truncateWithEllipsis(username ?? "", 16)}</p>
            <p className="role">
              {typeof roles === "object"
                ? roles.map(item => t(`${item}`)).join(", ")
                : roles}
            </p>
          </div>
          <button
            style={{
              cursor: `${
                isTouchDevice || !isLargeDevice ? "pointer" : "unset"
              }`,
            }}
            className="popup-btn"
            onClick={() => {
              if (isTouchDevice || !isLargeDevice) {
                setShowUserInfo(!showUserInfo);
                return;
              }
              return;
            }}>
            <div className="avatar">
              <span>{getInitials(username ?? "")}</span>
            </div>
            <div className="text-content">
              <p className="name">{truncateWithEllipsis(username ?? "", 16)}</p>
              <p className="role">
                {/* //* Need to check first in case there are still multiple roles in single string format */}
                {typeof roles === "object"
                  ? roles.map(item => t(`${item}`)).join(", ")
                  : roles}
              </p>
            </div>
          </button>
        </li>
      </ul>
    </StyledSidebar>
  );
};
